import { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import {
  DEFAULT_GROUP_WORK_LOCATION,
  type GroupWorkLocation,
} from '../../constants/groupWorkLocation';
import GroupWorkLocationField from './GroupWorkLocationField';


interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  class?: string;
  title?: string;
}


export default function GroupCreate() {
  const navigate = useNavigate();
  const location = useLocation();
  const groupsListSearch = (location.state as { groupsListSearch?: string } | null)?.groupsListSearch;
  const groupsListPath =
    groupsListSearch !== undefined ? `/groups${groupsListSearch}` : '/groups';
  const { user: currentUser, isAdmin } = useAuth();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>('');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type: 'assignment',
    groupType: 'itil',
    email: '',
    managerId: '',
    timezone: 'UTC',
    escalationLevel: 1,
    autoAssign: false,
    skills: [] as string[],
    defaultHourlyRate: 0,
    workLocation: DEFAULT_GROUP_WORK_LOCATION as GroupWorkLocation,
    isActive: true
  });
  const [availableUsers, setAvailableUsers] = useState<User[]>([]);

  useEffect(() => {
    fetchAvailableData();
  }, []);

  const fetchAvailableData = async () => {
    try {
      const usersRes = await api.get('/users').catch(() => ({ data: [] }));
      setAvailableUsers(usersRes.data.users || usersRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!formData.name) {
      setError('Group name is required');
      return;
    }

    if (!formData.defaultHourlyRate || formData.defaultHourlyRate <= 0) {
      setError('Default Hourly Rate is required and must be greater than 0');
      return;
    }

    setError('');
    setSaving(true);
    try {
      const response = await api.post('/groups', formData);
      navigate(`/groups/${response.data.id}`, { state: location.state });
    } catch (error: any) {
      console.error('Error creating group:', error);
      const errorMessage = error.response?.data?.message || 'Failed to create group';
      setError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  // Only show page to admin and super_admin
  if (!currentUser || !isAdmin()) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">You don't have permission to access this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <Link
          to={groupsListPath}
          className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-2" />
          Back to Groups
        </Link>
        
        <div className="flex justify-between items-start">
          <h1 className="text-2xl font-bold text-gray-900">New Group</h1>
          
          <div className="flex gap-2">
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
            >
              {saving ? 'Creating...' : 'Create Group'}
            </button>
            <Link
              to={groupsListPath}
              className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
            >
              Cancel
            </Link>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              className="w-full px-3 py-2 border rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="Enter group name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Type *
            </label>
            <select
              value={formData.type}
              onChange={(e) => handleInputChange('type', e.target.value)}
              className="w-full px-3 py-2 border rounded-md focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="assignment">Assignment</option>
              <option value="approval">Approval</option>
              <option value="notification">Notification</option>
              <option value="peer_review">Peer Review</option>
              <option value="customer_approver">Can Approve on behalf of Customer</option>
              <option value="assessment">Assessment</option>
              <option value="billing">Billing</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Group Type *
            </label>
            <select
              value={formData.groupType}
              onChange={(e) => handleInputChange('groupType', e.target.value)}
              className="w-full px-3 py-2 border rounded-md focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="catalog">Catalog</option>
              <option value="itil">ITIL</option>
              <option value="schedule_visible_externally">Schedule Visible Externally</option>
              <option value="schedule_visible_internally">Schedule Visible Internally</option>
              <option value="survey">Survey</option>
              <option value="service_desk">Service Desk</option>
            </select>
          </div>

          <div className="md:col-span-2">
            <GroupWorkLocationField
              value={formData.workLocation}
              onChange={(workLocation) => handleInputChange('workLocation', workLocation)}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Manager
            </label>
            <select
              value={formData.managerId}
              onChange={(e) => handleInputChange('managerId', e.target.value)}
              className="w-full px-3 py-2 border rounded-md focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="">Select Manager</option>
              {availableUsers.filter(user => user.title === 'Manager').map(user => (
                <option key={user.id} value={user.id}>
                  {user.firstName} {user.lastName} ({user.email})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Group Email
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              className="w-full px-3 py-2 border rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="group@example.com"
            />
          </div>


          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Timezone
            </label>
            <input
              type="text"
              value={formData.timezone}
              onChange={(e) => handleInputChange('timezone', e.target.value)}
              className="w-full px-3 py-2 border rounded-md focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Escalation Level
            </label>
            <input
              type="number"
              value={formData.escalationLevel}
              onChange={(e) => handleInputChange('escalationLevel', parseInt(e.target.value))}
              className="w-full px-3 py-2 border rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              min="1"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Default Hourly Rate *
            </label>
            <input
              type="number"
              value={formData.defaultHourlyRate}
              onChange={(e) => handleInputChange('defaultHourlyRate', parseFloat(e.target.value))}
              className="w-full px-3 py-2 border rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="0.00"
              step="0.01"
              min="0"
            />
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="autoAssign"
              checked={formData.autoAssign}
              onChange={(e) => handleInputChange('autoAssign', e.target.checked)}
              className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
            />
            <label htmlFor="autoAssign" className="ml-2 block text-sm text-gray-700">
              Auto Assign
            </label>
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="isActive"
              checked={formData.isActive}
              onChange={(e) => handleInputChange('isActive', e.target.checked)}
              className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
            />
            <label htmlFor="isActive" className="ml-2 block text-sm text-gray-700">
              Active
            </label>
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="Enter group description"
            />
          </div>
        </div>
      </div>
    </div>
  );
}